/**
 * ContractFormDialog (Phase 6, §6.2/§4.2). Create or edit a contract:
 * match key (Zahlungsempfänger / Unterhändler / Verwendungszweck / Konto),
 * forecast attribution (Kategorie), signed Betrag + Toleranz, Rhythmus,
 * Stichtag (anchor_date) / Endet am (ends_on), Aktiv, Notizen. Mechanics
 * mirror `pages/rules/RuleFormDialog.tsx` exactly: Modal + Field render-prop +
 * Input/Select/Textarea, the seedKey re-seed-during-render idiom (no effect),
 * and inline imperative validation via `setFormError`.
 *
 * Delete (edit mode only) lives inside this dialog behind a nested
 * `ConfirmDialog`, per §6.2 bullet 4.
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Textarea } from '../../components/ui/Textarea.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { BucketSelect } from '../../components/ui/BucketSelect.tsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { formatAmountInput, parseAmountInput } from '../../lib/format.ts';
import { useAccounts } from '../../lib/hooks/useAccounts.ts';
import { useCreateContract, useDeleteContract, useUpdateContract } from '../../lib/hooks/useContracts.ts';
import { CONTRACT_CADENCE } from '../../lib/labels.ts';
import type { ContractCadence, ContractCreate, ContractOut, Money } from '../../lib/types.ts';

/** Fields carried from a `ContractSuggestion` into a fresh, prefilled create. */
export interface ContractSuggestionPrefill {
  name: string;
  match_payee: string;
  match_sub_merchant: string | null;
  expected_amount: Money;
  anchor_date: string;
  cadence: ContractCadence;
  bucket_id: number | null;
}

const CADENCE_OPTIONS: SelectOption[] = (
  Object.entries(CONTRACT_CADENCE) as [ContractCadence, string][]
).map(([value, label]) => ({ value, label }));

const NO_ACCOUNT = '';

function todayIso(): string {
  return new Date().toLocaleDateString('sv-SE');
}

interface Draft {
  name: string;
  matchPayee: string;
  matchSubMerchant: string;
  matchPurposeContains: string;
  accountId: number | null;
  bucketId: number | null;
  expectedAmount: string;
  amountTolerance: string;
  cadence: ContractCadence;
  anchorDate: string;
  endsOn: string;
  active: boolean;
  notes: string;
}

/** 10 % of |expected|, rounded to cents — UI convenience default (§6.2). */
function tolerancePrefillWire(expectedWire: string): string {
  const n = Number.parseFloat(expectedWire);
  if (!Number.isFinite(n) || n === 0) return '0.00';
  const cents = Math.round(Math.abs(n) * 100 * 0.1);
  return (cents / 100).toFixed(2);
}

function buildDraft(contract?: ContractOut | null, initial?: ContractSuggestionPrefill | null): Draft {
  if (contract) {
    return {
      name: contract.name,
      matchPayee: contract.match_payee,
      matchSubMerchant: contract.match_sub_merchant ?? '',
      matchPurposeContains: contract.match_purpose_contains ?? '',
      accountId: contract.account_id,
      bucketId: contract.bucket_id,
      expectedAmount: formatAmountInput(contract.expected_amount),
      amountTolerance: formatAmountInput(contract.amount_tolerance),
      cadence: contract.cadence,
      anchorDate: contract.anchor_date,
      endsOn: contract.ends_on ?? '',
      active: contract.active,
      notes: contract.notes ?? '',
    };
  }
  if (initial) {
    return {
      name: initial.name,
      matchPayee: initial.match_payee,
      matchSubMerchant: initial.match_sub_merchant ?? '',
      matchPurposeContains: '',
      accountId: null,
      bucketId: initial.bucket_id,
      expectedAmount: formatAmountInput(initial.expected_amount),
      amountTolerance: formatAmountInput(tolerancePrefillWire(initial.expected_amount)),
      cadence: initial.cadence,
      anchorDate: initial.anchor_date,
      endsOn: '',
      active: true,
      notes: '',
    };
  }
  return {
    name: '',
    matchPayee: '',
    matchSubMerchant: '',
    matchPurposeContains: '',
    accountId: null,
    bucketId: null,
    expectedAmount: '',
    amountTolerance: '0,00',
    cadence: 'monthly',
    anchorDate: todayIso(),
    endsOn: '',
    active: true,
    notes: '',
  };
}

export interface ContractFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode. */
  contract?: ContractOut | null;
  /** Present → create mode prefilled (e.g. from a suggestion). */
  initial?: ContractSuggestionPrefill | null;
}

export function ContractFormDialog({ open, onOpenChange, contract, initial }: ContractFormDialogProps) {
  const { toast } = useToast();
  const accounts = useAccounts();
  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const deleteContract = useDeleteContract();
  const [draft, setDraft] = useState<Draft>(() => buildDraft(contract, initial));
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Re-seed the form when the dialog opens or switches target, without an
  // effect: adjust state during render off a signature (React docs pattern).
  const seedKey = open
    ? contract
      ? `c${contract.id}`
      : `i:${initial ? `${initial.name}:${initial.match_payee}` : 'new'}`
    : 'closed';
  const [lastSeedKey, setLastSeedKey] = useState('closed');
  if (seedKey !== lastSeedKey) {
    setLastSeedKey(seedKey);
    if (open) {
      setDraft(buildDraft(contract, initial));
      setFormError(null);
      setConfirmDeleteOpen(false);
    }
  }

  const pending = createContract.isPending || updateContract.isPending;

  function patch(next: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  const accountOptions: SelectOption[] = [
    { value: NO_ACCOUNT, label: 'Alle Konten' },
    ...accounts.map((a) => ({ value: String(a.id), label: a.name })),
  ];

  function submit() {
    if (!draft.name.trim()) {
      setFormError('Bitte einen Namen angeben.');
      return;
    }
    if (!draft.matchPayee.trim()) {
      setFormError('Bitte einen Zahlungsempfänger (Match) angeben.');
      return;
    }
    const expectedAmount = parseAmountInput(draft.expectedAmount);
    if (expectedAmount === null || Number(expectedAmount) === 0) {
      setFormError('Bitte einen gültigen Betrag ungleich 0 angeben.');
      return;
    }
    const amountTolerance = parseAmountInput(draft.amountTolerance || '0') ?? '0.00';
    if (Number(amountTolerance) < 0) {
      setFormError('Die Toleranz darf nicht negativ sein.');
      return;
    }
    if (!draft.anchorDate) {
      setFormError('Bitte einen Stichtag angeben.');
      return;
    }
    if (draft.endsOn && draft.endsOn < draft.anchorDate) {
      setFormError('„Endet am“ darf nicht vor dem Stichtag liegen.');
      return;
    }
    setFormError(null);

    const body: ContractCreate = {
      name: draft.name.trim(),
      match_payee: draft.matchPayee.trim(),
      match_sub_merchant: draft.matchSubMerchant.trim() || null,
      match_purpose_contains: draft.matchPurposeContains.trim() || null,
      account_id: draft.accountId,
      bucket_id: draft.bucketId,
      expected_amount: expectedAmount,
      amount_tolerance: amountTolerance,
      cadence: draft.cadence,
      anchor_date: draft.anchorDate,
      ends_on: draft.endsOn || null,
      active: draft.active,
      notes: draft.notes.trim() || null,
    };

    const onSuccess = () => {
      toast(contract ? 'Vertrag aktualisiert.' : 'Vertrag angelegt.', { variant: 'success' });
      onOpenChange(false);
    };
    const onError = (err: Error) => setFormError(err.message);

    if (contract) {
      updateContract.mutate({ id: contract.id, body }, { onSuccess, onError });
    } else {
      createContract.mutate(body, { onSuccess, onError });
    }
  }

  function confirmDelete() {
    if (!contract) return;
    const target = contract;
    deleteContract.mutate(target.id, {
      onSuccess: () => {
        toast(`Vertrag „${target.name}“ gelöscht.`, { variant: 'success' });
        setConfirmDeleteOpen(false);
        onOpenChange(false);
      },
      onError: (err) => toast(err.message, { variant: 'error' }),
    });
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={contract ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}
        size="lg"
        footer={
          <>
            {contract ? (
              <Button
                variant="danger"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={pending}
                className="mr-auto"
              >
                Löschen
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
              Abbrechen
            </Button>
            <Button onClick={submit} disabled={pending}>
              {pending ? 'Speichere …' : 'Speichern'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name" required>
              {(id) => (
                <Input
                  id={id}
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="z. B. Netflix, Miete"
                />
              )}
            </Field>
            <Field label="Zahlungsempfänger (Match)" required hint="normalisierter Empfänger">
              {(id) => (
                <Input
                  id={id}
                  value={draft.matchPayee}
                  onChange={(e) => patch({ matchPayee: e.target.value })}
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Unterhändler" hint="optional, z. B. PSP-Untermarke">
              {(id) => (
                <Input
                  id={id}
                  value={draft.matchSubMerchant}
                  onChange={(e) => patch({ matchSubMerchant: e.target.value })}
                />
              )}
            </Field>
            <Field label="Verwendungszweck enthält" hint="optional">
              {(id) => (
                <Input
                  id={id}
                  value={draft.matchPurposeContains}
                  onChange={(e) => patch({ matchPurposeContains: e.target.value })}
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Konto" hint="optional, schränkt den Match ein">
              {(id) => (
                <Select
                  id={id}
                  value={draft.accountId === null ? NO_ACCOUNT : String(draft.accountId)}
                  onValueChange={(v) => patch({ accountId: v === NO_ACCOUNT ? null : Number(v) })}
                  options={accountOptions}
                />
              )}
            </Field>
            <Field label="Kategorie" hint="nur für die Prognose, keine Kategorisierung">
              {(id) => (
                <BucketSelect
                  id={id}
                  value={draft.bucketId}
                  onChange={(bucketId) => patch({ bucketId })}
                  allowClear
                  placeholder="Keine Kategorie"
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Betrag" required hint="negativ = Kosten, positiv = Einnahme">
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={draft.expectedAmount}
                  onChange={(e) => patch({ expectedAmount: e.target.value })}
                  placeholder="-49,90"
                />
              )}
            </Field>
            <Field label="Toleranz" hint="absolute Abweichung, ab der ein Alarm ausgelöst wird">
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={draft.amountTolerance}
                  onChange={(e) => patch({ amountTolerance: e.target.value })}
                  placeholder="0,00"
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Rhythmus" required>
              {(id) => (
                <Select
                  id={id}
                  value={draft.cadence}
                  onValueChange={(v) => patch({ cadence: v as ContractCadence })}
                  options={CADENCE_OPTIONS}
                />
              )}
            </Field>
            <Field label="Stichtag" required hint="bekanntes Fälligkeitsdatum">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.anchorDate}
                  onChange={(e) => patch({ anchorDate: e.target.value })}
                />
              )}
            </Field>
            <Field label="Endet am" hint="optional, gekündigt zum">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.endsOn}
                  onChange={(e) => patch({ endsOn: e.target.value })}
                />
              )}
            </Field>
          </div>

          <Field label="Notizen">
            {(id) => (
              <Textarea
                id={id}
                rows={2}
                value={draft.notes}
                onChange={(e) => patch({ notes: e.target.value })}
              />
            )}
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
              checked={draft.active}
              onChange={(e) => patch({ active: e.target.checked })}
            />
            Aktiv
          </label>

          {formError ? <p className="text-sm text-rose-600 dark:text-rose-400">{formError}</p> : null}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Vertrag löschen"
        message={
          contract
            ? `„${contract.name}“ wirklich löschen? Bereits verknüpfte Umsätze bleiben erhalten, verlieren aber die Verknüpfung.`
            : ''
        }
        confirmLabel="Löschen"
        danger
        loading={deleteContract.isPending}
        onConfirm={confirmDelete}
      />
    </>
  );
}
